/**
 * The stock alpha platform's vocabulary — pure functions, no React.
 *
 * UNITS ARE NOT THE MF ENGINE'S. Alpha, specific volatility, weights, returns
 * and rates arrive as FRACTIONS (0.0016 is 16 bp, 0.085 is 8.5%), so this
 * file is the one place they are scaled for display.
 */

/* ── Horizons ─────────────────────────────────────────────── */

/**
 * Every horizon the server accepts, shortest first. Intraday and ten-year are
 * in the catalog but not modelled (no minute bars; no statistical content at
 * ten years) — offered so the toolbar reads as designed, and they say so.
 */
export const STOCK_HORIZONS = [
  { id: 'intraday', label: 'intraday' },
  { id: 'week', label: 'week' },
  { id: 'month', label: 'month' },
  { id: 'six_month', label: '6m' },
  { id: 'year', label: '1y' },
  { id: 'three_to_five_year', label: '3-5y' },
  { id: 'ten_year', label: '10y' },
];

export const UNMODELLED_HORIZONS = new Set(['intraday', 'ten_year']);
export const MODELLED_HORIZONS = STOCK_HORIZONS.filter((h) => !UNMODELLED_HORIZONS.has(h.id));
export const DEFAULT_STOCK_HORIZON = 'month';
export const stockHorizonLabel = (id) => STOCK_HORIZONS.find((h) => h.id === id)?.label ?? id ?? '—';

/** The minimum-percentile filter's choices; 0 shows the whole ranked list. */
export const MIN_PERCENTILE_OPTIONS = [0, 50, 80, 90, 95];

/* ── The desk's screens ───────────────────────────────────── */

/**
 * In the order a PM works through a day (backend docs/alpha_platform/30).
 * Each is its own route under /stock-alpha and its own row in the sidebar's
 * "Stock Alpha Ranker" section (config/navigation.js). `key` is the keyboard
 * shortcut.
 */
export const DESK_SCREENS = [
  { id: 'today', label: 'Today', key: '0' },
  { id: 'ranks', label: 'Ranks', key: '1' },
  { id: 'why', label: 'Why this stock', key: '2' },
  { id: 'books', label: 'Books', key: '3' },
  { id: 'theses', label: 'Theses', key: '4' },
  { id: 'paper', label: 'Paper trading', key: '5' },
  { id: 'health', label: 'Health', key: '6' },
  { id: 'factors', label: 'Factors', key: '7' },
  { id: 'research', label: 'Alpha Research', key: '8' },
  { id: 'optimiser', label: 'Exposure optimiser', key: '9' },
  { id: 'mine', label: 'My portfolios', key: 'M' },
];

export const STOCK_ALPHA_BASE = '/stock-alpha';
export const deskPath = (id) => `${STOCK_ALPHA_BASE}/${id}`;

/* ── Number formatting ────────────────────────────────────── */

const finite = (v) => v != null && v !== '' && Number.isFinite(Number(v));

/** A return fraction in basis points, signed: 0.0016 → "+16". */
export function alphaBp(value) {
  if (!finite(value)) return '—';
  const bp = Math.round(Number(value) * 10000);
  return `${bp > 0 ? '+' : ''}${bp}`;
}

/** A fraction as a percent: 0.085 → "8.5%". */
export function fracPct(value, decimals = 1) {
  if (!finite(value)) return '—';
  return `${(Number(value) * 100).toFixed(decimals)}%`;
}

/** A fraction as a signed percent: 0.093 → "+9.3%". */
export function signedFracPct(value, decimals = 1) {
  if (!finite(value)) return '—';
  const v = Number(value) * 100;
  return `${v > 0 ? '+' : ''}${v.toFixed(decimals)}%`;
}

export const specVolPct = (v) => fracPct(v, 1);

/** A plain figure with fixed decimals, or a dash. */
export function fixed(value, decimals = 2) {
  return finite(value) ? Number(value).toFixed(decimals) : '—';
}

/** Up/down tone class for a signed figure. */
export const signTone = (v) => (Number(v) > 0 ? 'up' : Number(v) < 0 ? 'down' : '');

/** ISO date or datetime → YYYY-MM-DD. */
export const date10 = (v) => (v ? String(v).slice(0, 10) : '—');

/** ISO datetime → "YYYY-MM-DD HH:MM". */
export const dateTime16 = (v) => (v ? String(v).replace('T', ' ').slice(0, 16) : '—');

/** `snake_case` / `kebab-case` ids as words. */
export const humanise = (s) => String(s ?? '').replace(/[_-]+/g, ' ').trim();

/* ── Ranks ────────────────────────────────────────────────── */

/** The term structure as [sessions, value] points sorted by holding period. */
export function termPoints(termStructure) {
  if (!termStructure || typeof termStructure !== 'object') return [];
  return Object.entries(termStructure)
    .map(([sessions, value]) => [Number(sessions), Number(value)])
    .filter(([s, v]) => Number.isFinite(s) && Number.isFinite(v))
    .sort((a, b) => a[0] - b[0]);
}

/** The server's conflict flags. `none` is no flag at all. */
export const CONFLICT_LABEL = {
  trade_only_avoid_owning: 'Trade only — short-term positive, long-term negative',
  long_term_accumulate_on_weakness: 'Accumulate on weakness — long-term positive, short-term negative',
};

export const hasConflict = (flag) => Boolean(flag) && flag !== 'none';

/** The largest contributions by size, whichever way they push. */
export function topFactors(explanations, n = 3) {
  return [...(explanations ?? [])]
    .sort((a, b) => Math.abs(Number(b.contribution)) - Math.abs(Number(a.contribution)))
    .slice(0, n);
}

/* ── Personas ─────────────────────────────────────────────── */

/** Short chip names, keyed by the server's persona id; the full name is the tooltip. */
export const PERSONA_SHORT = {
  aqr_multi_style: 'AQR',
  buffett_munger_quality: 'Buffett',
  coffee_can: 'Coffee Can',
  fisher_growth: 'Fisher',
  forensic_red_flags: 'Red flags',
  graham_defensive: 'Graham',
  graham_net_net: 'Net-net',
  greenblatt_magic_formula: 'Greenblatt',
  low_volatility_conservative: 'Low vol',
  lynch_garp: 'Lynch',
  minervini_trend: 'Minervini',
  oneil_canslim: "O'Neil",
  piotroski_value: 'Piotroski',
  qglp: 'QGLP',
  short_term_quant: 'Quant',
  terry_smith_compounder: 'Terry Smith',
};

/** Personas whose PASS is a warning, not an endorsement. */
export const AVOID_PERSONAS = new Set(['forensic_red_flags']);

export const personaShort = (p) => PERSONA_SHORT[p.personaId] ?? p.displayName ?? p.personaId;

/** Full passes only, endorsements first — a partial pass is too weak to tag with. */
export function personaTags(personas) {
  return (personas ?? [])
    .filter((p) => p.status === 'pass')
    .sort((a, b) => Number(AVOID_PERSONAS.has(a.personaId)) - Number(AVOID_PERSONAS.has(b.personaId)));
}

/* ── Books & paper ────────────────────────────────────────── */

const capitalise = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

/**
 * A book's id as words: `view:momentum_breakout` → "Momentum breakout · view",
 * `pure_alpha:large:long_only` → "Pure alpha · large · long only".
 */
export function bookLabel(portfolioId) {
  const id = String(portfolioId ?? '').replace(/^paper:/, '').replace(/:sim$/, '');
  const [head, ...rest] = id.split(':');
  if (head === 'view') return `${capitalise(humanise(rest.join(' ')))} · view`;
  if (head === 'mandate') return `${capitalise(humanise(rest.join(' ')))} · mandate`;
  return [capitalise(humanise(head)), ...rest.map(humanise)].join(' · ');
}

/** The query arguments that select one book: views by id, everything else as a mandate. */
export function bookArgs(portfolioId) {
  const id = String(portfolioId ?? '');
  if (id.startsWith('view:')) return { viewId: id.slice(5), mandateId: null };
  return { viewId: null, mandateId: id.startsWith('mandate:') ? id.slice(8) : id };
}

/** A NAV series rebased to 1.0 at its first point. */
export function rebase(series, pick = (p) => p.nav) {
  const pts = (series ?? []).filter((p) => finite(pick(p)));
  if (!pts.length) return [];
  const base = Number(pick(pts[0])) || 1;
  return pts.map((p) => ({ date: date10(p.tradeDate), value: Number(pick(p)) / base }));
}

/** Total return across a NAV series, as a fraction. */
export function navReturn(series) {
  const pts = (series ?? []).filter((p) => finite(p.nav));
  if (pts.length < 2) return null;
  return Number(pts[pts.length - 1].nav) / Number(pts[0].nav) - 1;
}

/** ₹ in crore, for capital and NAV in rupees. */
export function inrCr(value, decimals = 2) {
  if (!finite(value)) return '—';
  return `₹${(Number(value) / 1e7).toLocaleString('en-IN', { maximumFractionDigits: decimals })} cr`;
}

/* ── Theses ───────────────────────────────────────────────── */

/** The review path, in order; `superseded` versions are history, not queue. */
export const THESIS_STATUSES = [
  'draft', 'needs_human', 'analyst_reviewed', 'compliance_approved', 'published', 'rejected',
];

/* ── Freshness ────────────────────────────────────────────── */

/** Days between an ISO date and today; null when there is no date. */
export function daysOld(isoDate, today = new Date()) {
  if (!isoDate) return null;
  const d = new Date(`${String(isoDate).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((today.getTime() - d.getTime()) / 86400000);
}

/** Calendar days past which a ranking reads as stale — about two trading days. */
export const STALE_AFTER_DAYS = 4;

/**
 * Is the data safe to act on? There is no "paused" field on the server (B10),
 * so this reads it from job health when the seat may see it — every job
 * missing and none completed is a pipeline switched off, not failing — and
 * from the ranking's own age otherwise.
 *
 * Returns `{ state: 'ok' | 'paused' | 'unhealthy' | 'stale' | 'unknown', since, age, jobs }`.
 */
export function pipelineState(health, asOf) {
  const age = daysOld(asOf);
  if (health?.available) {
    const jobs = health.jobs ?? [];
    const allMissing = jobs.length > 0 && jobs.every((j) => Number(j.completed) === 0 && Number(j.missing) > 0);
    if (allMissing) return { state: 'paused', since: health.since, age, jobs };
    if (health.healthy === false) return { state: 'unhealthy', since: health.since, age, jobs: jobs.filter((j) => !j.healthy) };
    if (health.healthy === true) return { state: 'ok', since: null, age, jobs: [] };
  }
  if (age == null) return { state: 'unknown', since: null, age, jobs: [] };
  return { state: age > STALE_AFTER_DAYS ? 'stale' : 'ok', since: null, age, jobs: [] };
}

/* ── Permissions ──────────────────────────────────────────── */

/**
 * Did the server refuse this read for want of a role, rather than fail? The
 * alpha platform's staff reads answer 403 "requires one of functional roles
 * […]" or an employee-check refusal; a screen shows that as a role note, not
 * as an error.
 */
export function isRoleRefusal(error) {
  const messages = [
    error?.message,
    ...(error?.graphQLErrors ?? []).map((e) => e?.message),
  ].filter(Boolean).join(' ');
  return /functional role|org role|not an employee|employee|research.?analyst|research.?lead|not permitted/i.test(messages);
}
